import * as React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import TestResultConversationMessage from './TestResultConversationMessage';
import { figmaColors } from '../colors';

export interface CaptionData {
  start: number;
  end: number;
  text: string;
}

interface Character {
  id: string;
  name: string;
  avatarUrl: string;
}

export interface CaptionWithCharacter {
  data: CaptionData;
  character: Character;
}

export interface ConversationScriptProps {
  subtitle?: CaptionWithCharacter[];
  explanation?: any;
  characters: Character[];
  audioUrl?: string;
  onUpdateCaption?: (index: number, caption: CaptionWithCharacter) => void;
  isValidStartTime?: (index: number) => boolean;
  isValidEndTime?: (index: number) => boolean;
  peaks?: number[];
  progressInSeconds?: number;
}

const TestResultConversationScript = observer((props: ConversationScriptProps) => {
  const { subtitle, characters, progressInSeconds } = props;
  const [highlightIndex, setHighlightIndex] = React.useState(undefined);

  const getHighlightIndex = React.useCallback(
    (playedMilliseconds) => {
      const index = subtitle.findIndex((it) => {
        const {
          data: { start, end },
        } = it;
        return start <= playedMilliseconds && end >= playedMilliseconds;
      });
      if (index !== -1) {
        setHighlightIndex(index);
      }
    },
    [subtitle]
  );

  React.useEffect(() => {
    getHighlightIndex(progressInSeconds * 1000);
  }, [progressInSeconds, getHighlightIndex]);

  return (
    <ConversationScriptContainer>
      <MainSubtitle>
        <SubtitleContainer>
          {subtitle.map((caption, index) => {
            const { data, character } = caption;
            return (
              <MessageContainer key={index}>
                <TestResultConversationMessage
                  characterIndex={characters.findIndex((c) => c.id === character?.id)}
                  data={data}
                  character={character}
                  isHighlight={highlightIndex === index}
                />
              </MessageContainer>
            );
          })}
        </SubtitleContainer>
      </MainSubtitle>
    </ConversationScriptContainer>
  );
});

const ConversationScriptContainer = styled.div`
  display: grid;
  grid-auto-flow: row;
  padding-bottom: 16px;
`;

const MainSubtitle = styled.div`
  background: ${figmaColors.W2B1};
`;

const SubtitleContainer = styled.div`
  padding-bottom: 42px;
`;

const MessageContainer = styled.div`
  margin-bottom: 16px;
  :last-child {
    margin-bottom: 0;
  }
`;

export default React.memo(TestResultConversationScript);
